'use client';

import { Spinner } from '@common/components';
import { useToast } from '@common/hooks';
import { SvgAdd, SvgLink } from '@common/icon';
import { cn, isDev } from '@core/utils';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Image } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { ENV, SEARCH_PARAMS } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useClipboard } from '@/hooks/useClipboard';
import { useKakaoShare } from '@/hooks/useKakaoShare';
import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';
import { useAuthStore } from '@/stores/AuthStore';
import { useDeviceInfoStore, WEBVIEW_TYPE } from '@/stores/DeviceStore';
import { useGlobalStore } from '@/stores/GlobalStore';

interface KakaoWindow extends Window {
  Kakao?: {
    isInitialized?: () => boolean;
    init?: (clientId: string) => void;
    Share?: {
      sendScrap?: (options: { requestUrl: string }) => void;
    };
  };
}

export const ShareDrawer = () => {
  const [webviewType, osType] = useDeviceInfoStore(
    useShallow((state) => [state.webviewType, state.osType]),
  );

  const { isWelloApp } = useCheckWelloApp();
  const { isInitialized } = useKakaoShare();

  const [shareLink, setShareLink] = useGlobalStore(
    useShallow((state) => [state.shareLink, state.setShareLink]),
  );

  const memberCode = useAuthStore((state) => state.myInfo?.memberCode);

  const shareLinkUrl = (() => {
    if (!shareLink) return;

    const pathname = new URL(shareLink).pathname;

    let mainDomain = ENV.NEXT_PUBLIC_DOMAIN;

    if (!isDev) {
      mainDomain = mainDomain.replace('https://', 'https://www.');
    }

    const url = new URL(pathname, mainDomain);

    url.searchParams.set(SEARCH_PARAMS.ENTER_TYPE, 'share');

    if (memberCode) {
      url.searchParams.set(SEARCH_PARAMS.SHARE_BY, memberCode);
    }

    return url.href;
  })();

  const copy = useClipboard();

  const {
    request: checkKakaoInstallRequest,
    response: checkKakaoInstallResponse,
  } = useHandleApp({
    type: MESSAGE_KEYWORD.CHECK_KAKAO_INSTALLED,
  });

  const {
    request: requestShare,
    isPending,
    response,
  } = useHandleApp({
    type: MESSAGE_KEYWORD.SHARE_CONTENT,
  });

  const isKakaoShareResponse = response?.params?.shareType;

  useEffect(() => {
    if (isKakaoShareResponse) {
      setShareLink(null);
    }
  }, [isKakaoShareResponse, setShareLink]);

  const { toast } = useToast();

  const isMobileWeb =
    !webviewType && osType && ['ios', 'android'].includes(osType);

  const isOpened = !!shareLink;

  useEffect(() => {
    if (isOpened) {
      checkKakaoInstallRequest();
    }
  }, [checkKakaoInstallRequest, isOpened]);

  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    setShareLink(null);
  }, [pathname, searchParams, setShareLink]);

  const handleKakaoClickInApp = async () => {
    if (isPending) return;

    if (checkKakaoInstallResponse?.isSuccess && shareLinkUrl) {
      requestShare({
        shareType: 'kakao',
        url: shareLinkUrl,
      });

      setShareLink(null);
    } else {
      toast({
        message: '카카오톡이 설치되어 있지 않아요',
        type: 'fail',
      });
    }
  };

  const handleKakaoClickInWeb = async () => {
    if (!shareLinkUrl) return;

    const kakao = (window as KakaoWindow).Kakao;

    kakao?.Share?.sendScrap?.({
      requestUrl: shareLinkUrl,
    });

    setShareLink(null);
  };

  return (
    <ResponsiveDialog
      opened={!!shareLink}
      onClose={() => setShareLink(null)}
      drawerType="bottom"
      className={{ modal: 'w-full max-w-[480px]' }}
    >
      <div
        className={cn('mx-42 flex justify-around gap-6 pb-16', {
          'justify-between': isWelloApp,
        })}
      >
        {webviewType !== WEBVIEW_TYPE.KB_PAY ? (
          <KakaoButton
            isLoading={!isInitialized}
            onClick={isWelloApp ? handleKakaoClickInApp : handleKakaoClickInWeb}
          />
        ) : null}
        <button
          type="button"
          className="flex cursor-pointer flex-col items-center justify-center gap-6 border-none"
          onClick={() => {
            try {
              if (shareLinkUrl) {
                return copy({
                  value: shareLinkUrl,
                });
              }
            } finally {
              setShareLink(null);
            }
          }}
        >
          <div className="flex size-48 items-center justify-center rounded-pill bg-Gray-500 text-Gray-white">
            <SvgLink />
          </div>
          <h4 className="font-400 text-Gray-900 typo-body4">URL 복사</h4>
        </button>
        {isWelloApp || isMobileWeb ? (
          <button
            type="button"
            className="flex cursor-pointer flex-col items-center justify-center gap-6 border-none"
            onClick={() => {
              try {
                if (!shareLinkUrl) return;

                if (isWelloApp)
                  return requestShare({
                    shareType: 'uni',
                    url: shareLinkUrl,
                  });

                if (isMobileWeb)
                  return navigator.share({
                    url: shareLinkUrl,
                  });
              } finally {
                setShareLink(null);
              }
            }}
          >
            <div className="flex size-48 items-center justify-center rounded-pill bg-Gray-200 text-Gray-black">
              <SvgAdd />
            </div>
            <h4 className="font-400 text-Gray-900 typo-body4">더 보기</h4>
          </button>
        ) : null}
      </div>
    </ResponsiveDialog>
  );
};

interface KakaoButtonProps {
  isLoading: boolean;
  onClick: () => void;
}

const KakaoButton = ({ isLoading, onClick }: KakaoButtonProps) => {
  return (
    <button
      type="button"
      disabled={isLoading}
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-6 border-none',
        { 'cursor-not-allowed opacity-50': isLoading },
      )}
      onClick={onClick}
    >
      <div className="flex size-48 items-center justify-center rounded-pill bg-[#fbdf03]">
        {isLoading ? (
          <Spinner size={24} />
        ) : (
          <Image
            alt="kakao"
            height={24}
            src="/assets/pngs/login/kakao.png"
            width={24}
          />
        )}
      </div>
      <span className="font-400 text-Gray-900 typo-body4">카카오톡</span>
    </button>
  );
};
